import { useCallback, useEffect, useState } from 'react';
import GameScreen from './components/GameScreen';
import HomeScreen from './components/HomeScreen';
import { preloadSphereViewer } from './components/panorama/controls';
import Summary from './components/Summary';
import { loadSettings, sanitizeSettings, saveSettings, type GameSettings } from './game/settings';
import { clearStats, emptyStats, loadStats, type PlayerStats } from './game/stats';
import { useGame } from './hooks/useGame';

export default function App() {
  const [settings, setSettings] = useState<GameSettings>(loadSettings);
  const [stats, setStats] = useState<PlayerStats>(loadStats);
  const game = useGame(settings, setStats);
  const { state, start, goHome } = game;

  const changeSettings = useCallback((next: GameSettings) => {
    setSettings(next);
    saveSettings(next);
  }, []);

  // Download the 360° viewer in the background while the player is on the home screen.
  useEffect(() => {
    const timer = window.setTimeout(preloadSphereViewer, 1500);
    return () => window.clearTimeout(timer);
  }, []);

  const resetStats = useCallback(() => {
    clearStats();
    setStats(emptyStats());
  }, []);

  // A source can become unusable during the session (e.g. Google key rejected): repair the choice.
  const usableSettings = sanitizeSettings(settings);

  if (state.phase === 'home') {
    return (
      <HomeScreen
        settings={usableSettings}
        onChange={changeSettings}
        onPlay={() => start(usableSettings)}
        stats={stats}
        onClearStats={resetStats}
      />
    );
  }

  if (state.phase === 'summary') {
    return <Summary state={state} onReplay={() => start(usableSettings)} onHome={goHome} />;
  }

  return <GameScreen game={game} />;
}
